// @flow

import Url from 'url-parse';
import queryString from 'query-string';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { MetaService } from 'shared_services/riseart/meta/Meta';
import { meta as META_ENUM } from 'Enum';

const {
  METATYPE: { META_TITLE, LINK_NEXT, LINK_PREV, LINK_CANONICAL },
  SUBSCRIBER_NAME: { PAGINATION_META: PAGINATION_META_SUBSCRIBER },
} = META_ENUM;

/**
 * collectPaginationMetaData
 *
 * @param {string} baseUrl
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {boolean} regenerateMeta
 */
export function collectPaginationMetaData(
  baseUrl: string,
  currentPage: number = 1,
  totalPages: number = 0,
  regenerateMeta: boolean = false,
  translationFormatter?: Function = null,
) {
  if (!baseUrl) {
    return;
  }

  const metaData = [];
  const parsedUrl = new Url(baseUrl);
  const parsedQueryString = queryString.parse(parsedUrl.query);
  const unresolveSubscriber =
    regenerateMeta && MetaService.isSubscriberResolved(PAGINATION_META_SUBSCRIBER);
  const translatedPageQueryStringParameter = LocalizedConfig.get(
    'navigation.uri.query.params.page',
  );

  // Unresolve subscriber and remove prev/next,
  // and leave the rest of the function to update them again
  if (unresolveSubscriber) {
    MetaService.unresolveSubscription(PAGINATION_META_SUBSCRIBER);
    MetaService.removeByType(LINK_PREV);
    MetaService.removeByType(LINK_NEXT);
  }

  // Canonical link
  if (currentPage > 1 || unresolveSubscriber) {
    const { page, ...qsParams } = parsedQueryString;
    const params = queryString.stringify({
      ...qsParams,
      ...(currentPage > 1 ? { [translatedPageQueryStringParameter]: currentPage } : {}),
    });
    metaData.push({
      type: LINK_CANONICAL,
      value: `${parsedUrl.origin}${parsedUrl.pathname}${params ? `?${params}` : ''}`,
    });
  }

  // Prev link
  if (currentPage > 1 && currentPage <= totalPages) {
    const { page, ...qsParams } = parsedQueryString;
    const params = queryString.stringify({
      ...qsParams,
      ...(currentPage > 2 ? { [translatedPageQueryStringParameter]: currentPage - 1 } : {}),
    });
    metaData.push({
      type: LINK_PREV,
      value: `${parsedUrl.origin}${parsedUrl.pathname}${params ? `?${params}` : ''}`,
    });
  }

  // Next link
  if (currentPage > 0 && currentPage < totalPages) {
    const params = queryString.stringify({
      ...parsedQueryString,
      [translatedPageQueryStringParameter]: currentPage + 1,
    });
    metaData.push({
      type: LINK_NEXT,
      value: `${parsedUrl.origin}${parsedUrl.pathname}${params ? `?${params}` : ''}`,
    });
  }

  if (currentPage > 1) {
    metaData.push({
      type: META_TITLE,
      value: `${
        translationFormatter && typeof translationFormatter === 'function'
          ? translationFormatter({ id: 'components.pagination.page' })
          : 'Page'
      } ${currentPage}`,
    });
  }

  MetaService.add(metaData, PAGINATION_META_SUBSCRIBER);
  return metaData;
}
